import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Badge, message, Tag } from 'antd';
import {
  getTrainingAssessmentById,
  getCertificate,
  getQCCertificate,
} from '../services/ApiActions/lmsAct';
import { downloadFile } from '../helper/UtilityMethods';
import Loader from '../components/Layout/Loader';
import AssessmentCard from '../components/LMSComponent/AssesmentCard';
import CertificateCard from '../components/LMSComponent/CertificateCard';
import PageHeading from '../components/Layout/PageHeading';
import PageSection from '../components/LMSComponent/PageSection';
import LMSBreadcrumb from '../components/LMSComponent/LMSBreadcrumb';

// Improved type definitions
interface AssessmentData {
  id: string;
  name: string;
  reattempt_enabled: boolean;
}

interface CertificateDetails {
  is_lock: boolean;
  certificate_id?: string;
}

interface CourseData {
  id: string;
  courseName: string;
  assessment_data: AssessmentData;
  certificate_details: CertificateDetails;
}

const AssessmentPage: React.FC = () => {
  const navigate = useNavigate();
  const [messageApi, contextHolder] = message.useMessage();
  const { courseId } = useParams<{ courseId: string }>();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [courseData, setCourseData] = useState<CourseData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchCourseData = async () => {
      if (!courseId) {
        setError('Course ID is missing');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);

        const response: any = await getTrainingAssessmentById(courseId);

        if (response.status === 'success' && response.data) {
          // Store course name in session storage
          sessionStorage.setItem(
            'course_name',
            response.data.assessment_data.name
          );
          sessionStorage.setItem('courses', JSON.stringify(response.data));

          setCourseData(response.data);
        } else {
          setError(response.message || 'Failed to fetch assessment data');
        }
      } catch (error) {
        console.error('Error fetching assessment data:', error);
        setError(
          'Something went wrong while fetching assessment data. Please try again later.'
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchCourseData();
  }, [courseId]);

  const handleAssessmentClick = (assessmentId: string, reattempt?: boolean) => {
    let url = `/lms/start-training-assessment/${assessmentId}`;
    if (reattempt) {
      url += '?reattempt=true';
    }
    navigate(url);
  };

  const handleCertificateDownload = async (assessmentId: string) => {
    try {
      const certificate: any = await getQCCertificate(assessmentId);

      if (!certificate) {
        messageApi.error('Certificate data is empty.');
        return;
      }
      const downloadInfo = {
        blobData: certificate,
        contentType: 'application/pdf',
        fileNameWithExt: `certificate-${courseId}.pdf`,
      };
      downloadFile(downloadInfo);
    } catch (error) {
      console.error('Error downloading certificate:', error);
      messageApi.error(
        'Failed to download certificate. Please try again later.'
      );
    }
  };

  // Early return for loading state
  if (isLoading) {
    return <Loader style={{ marginTop: '20%', justifyContent: 'center' }} />;
  }

  // Early return for error state
  if (error || !courseData) {
    return (
      <div style={{ textAlign: 'center', marginTop: '20%' }}>
        <h2>Error</h2>
        <p>{error || 'No course data available'}</p>
        <button
          onClick={() => navigate('/lms')}
          style={{ padding: '8px 16px', marginTop: '16px' }}
        >
          Return to Courses
        </button>
      </div>
    );
  }

  // Destructure course data for cleaner JSX
  const { courseName, assessment_data, certificate_details } = courseData;
  const { id, name, reattempt_enabled } = assessment_data;
  const isCertificateLocked = certificate_details?.is_lock;

  return (
    <div>
      {contextHolder}
      <PageHeading heading={name} onBackClick={() => navigate('/lms')} />

      <LMSBreadcrumb courseName={name} />

      <PageSection
        heading="Assessment"
        description="Certification will be open for specific duration"
      >
        <AssessmentCard
          id={id}
          name={name}
          isLocked={false}
          isReattemptEnabled={reattempt_enabled} //reattempt_enabled
          onReattemptClick={(id) => handleAssessmentClick(id, true)}
          onAssesmentCardClick={(id) => handleAssessmentClick(id)}
          imgSrc="/images/assesment.png"
        />
      </PageSection>

      <PageSection
        heading="Certificate"
        description={
          isCertificateLocked
            ? 'Your certificate will be available for download once your assignment has been reviewed.'
            : undefined
        }
      >
        <CertificateCard
          isLock={isCertificateLocked}
          id={id}
          onAssesmentCardClick={handleCertificateDownload}
        />
      </PageSection>
    </div>
  );
};

export default AssessmentPage;
